import { useDroppable } from '@dnd-kit/core';
import { Box } from '@mui/material';
import { FC, KeyboardEvent, memo, MouseEvent, useMemo } from 'react';
import { VEHICLE_CARD_HEIGHT } from 'src/components/dispatch/vehicleCardStyles';
import { DispatchCard, Vehicle } from 'src/entities/dispatch';
import { GeoAlertInfo } from 'src/utils/geoAlerts';

import { DisplayMetric } from './DisplayMetricToggle';
import { VehicleRowCards } from './VehicleRowCards';

type GroupPosition = 'single' | 'start' | 'middle' | 'end';

interface VehicleRowViewProps {
  vehicle: Vehicle;
  rowId: string;
  cards: DispatchCard[];
  enabled?: boolean;
  duplicateStoreNames?: Set<string>;
  routeDeviationCardIds?: Set<string>;
  geoAlerts?: Map<string, GeoAlertInfo>;
  displayMetrics?: Set<DisplayMetric>;
  groupPosition?: GroupPosition;
  /** Sticky left にあるときのみ true。中央ボードでは false */
  showVehicleInfo?: boolean;
  rowSelected?: boolean;
  onSelectCard?: (
    card: DispatchCard,
    vehicle: Vehicle,
    orderIndex: number,
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ) => void;
}

export const VehicleRowView: FC<VehicleRowViewProps> = memo(({
  vehicle,
  rowId,
  cards,
  enabled = true,
  duplicateStoreNames,
  routeDeviationCardIds,
  geoAlerts,
  displayMetrics,
  showVehicleInfo = false,
  onSelectCard,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: rowId,
    data: { rowId, vehicleId: vehicle.id },
    disabled: !enabled,
  });

  // isOver は dnd-kit の InternalContext 経由で over 対象が変わるたびに更新されるため、
  // この行は over の変化のたびに必ず再描画される (memo では避けられない)。
  // sx を useMemo で包むのは、その再描画のたびに新しいオブジェクトを作って
  // 子の VehicleRowCards の props 比較を無駄に壊さないため。
  const sx = useMemo(
    () => ({
      display: 'flex',
      height: VEHICLE_CARD_HEIGHT,
      minWidth: showVehicleInfo ? 0 : 'max-content',
      flex: showVehicleInfo ? 1 : 'none',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      transition: 'background-color 150ms',
      bgcolor: isOver ? 'hsl(var(--accent) / 0.35)' : 'hsl(var(--background) / 0.55)',
      opacity: enabled ? 1 : 0.6,
    }),
    [isOver, enabled, showVehicleInfo],
  );

  return (
    <Box ref={setNodeRef} sx={sx}>
      <VehicleRowCards
        rowId={rowId}
        vehicle={vehicle}
        cards={cards}
        duplicateStoreNames={duplicateStoreNames}
        routeDeviationCardIds={routeDeviationCardIds}
        geoAlerts={geoAlerts}
        displayMetrics={displayMetrics}
        onSelectCard={onSelectCard}
      />
    </Box>
  );
});

VehicleRowView.displayName = 'VehicleRowView';
